import { isUnreachableError } from './is-unreachable-error';

export type ReachabilityState = 'unknown' | 'online' | 'offline';

export interface ServerReachabilityOptions {
  pingIntervalMs?: number;
  pingTimeoutMs?: number;
  getNavigatorOnline?: () => boolean;
  probe: () => Promise<void>;
  onNavigatorOnline?: (cb: () => void) => () => void;
  onNavigatorOffline?: (cb: () => void) => () => void;
}

export interface ReachabilitySnapshot {
  state: ReachabilityState;
  isReachable: boolean;
  isConfirmedOffline: boolean;
}

export class ServerReachabilityMonitor {
  private state: ReachabilityState;
  private listeners = new Set<(snapshot: ReachabilitySnapshot) => void>();
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private unsubOnline: (() => void) | null = null;
  private unsubOffline: (() => void) | null = null;

  constructor(private readonly options: ServerReachabilityOptions) {
    const navOnline = options.getNavigatorOnline?.() ?? true;
    this.state = navOnline ? 'unknown' : 'offline';
  }

  get isReachable(): boolean {
    return this.state === 'online';
  }

  isConfirmedOffline(): boolean {
    return this.state === 'offline';
  }

  getSnapshot(): ReachabilitySnapshot {
    return {
      state: this.state,
      isReachable: this.isReachable,
      isConfirmedOffline: this.isConfirmedOffline(),
    };
  }

  subscribe(listener: (snapshot: ReachabilitySnapshot) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private setState(next: ReachabilityState): void {
    if (this.state === next) return;
    this.state = next;
    const snapshot = this.getSnapshot();
    this.listeners.forEach((l) => l(snapshot));
  }

  reportSuccess(): void {
    this.setState('online');
  }

  reportFailure(error: unknown): void {
    if (isUnreachableError(error)) {
      this.setState('offline');
    }
  }

  async runProbe(): Promise<void> {
    const timeoutMs = this.options.pingTimeoutMs ?? 5_000;
    try {
      await Promise.race([
        this.options.probe(),
        new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('probe timeout')), timeoutMs);
        }),
      ]);
      this.setState('online');
    } catch {
      this.setState('offline');
    }
  }

  start(): void {
    if (this.intervalId) return;

    void this.runProbe();

    const interval = this.options.pingIntervalMs ?? 30_000;
    this.intervalId = setInterval(() => void this.runProbe(), interval);

    if (this.options.onNavigatorOffline) {
      this.unsubOffline = this.options.onNavigatorOffline(() => this.setState('offline'));
    }
    if (this.options.onNavigatorOnline) {
      this.unsubOnline = this.options.onNavigatorOnline(() => {
        this.setState('unknown');
        void this.runProbe();
      });
    }
  }

  stop(): void {
    if (this.intervalId) clearInterval(this.intervalId);
    this.intervalId = null;
    this.unsubOnline?.();
    this.unsubOffline?.();
    this.unsubOnline = null;
    this.unsubOffline = null;
  }
}
